'use client';

import { type ReactNode } from 'react';

import { type VariantProps } from 'tailwind-variants';

import {
  Typography,
  type TypographyProps,
} from '../../../components/typography/Typography';
import { type Component, type ComponentProps } from '../../../types/component';
import { tv } from '../../../utils/tv';
import { FormDescription, type FormDescriptionProps } from './FormDescription';

type Variant = VariantProps<typeof formControlLabelStyles>;

const resolveVariant = (
  size: FormControlLabelBaseProps['size']
): TypographyProps['variant'] => {
  return size === 'md' ? 'body1' : 'body2';
};

const formControlLabelClasses = {
  root: 'zds-form-control-label-root',
  content: 'zds-form-control-label-content',
  label: 'zds-form-control-label-label',
  labelText: 'zds-form-control-label-text',
  requiredIndicator: 'zds-form-control-label-required-indicator',
  endLabel: 'zds-form-control-label-end-label',
  leftSlot: 'zds-form-control-label-left-slot',
} as const;

const formControlLabelStyles = tv({
  slots: {
    root: [
      formControlLabelClasses.root,
      'flex flex-1 items-start z--form-control-label-gap',
    ],
    content: [formControlLabelClasses.content, 'flex flex-1 flex-col'],
    label: [
      formControlLabelClasses.label,
      'flex flex-1 items-start z--form-control-label-gap',
    ],
    labelText: [formControlLabelClasses.labelText],
    requiredIndicator: [
      formControlLabelClasses.requiredIndicator,
      'ml-[var(--z--form-control-label-gap)]',
    ],
    endLabel: [formControlLabelClasses.endLabel, 'ml-auto shrink-0'],
    leftSlot: [
      formControlLabelClasses.leftSlot,
      'flex',
      'items-center',
      'shrink-0',
    ],
    description: [
      'z--form-control-label-description-margin-top',
      'z--form-control-label-description-padding-left',
    ],
  },
  variants: {
    color: {
      primary: {
        endLabel: 'text-secondary',
        requiredIndicator: 'text-error-main',
      },
      white: {},
    },
    isDisabled: {
      true: {
        root: 'disabled-foreground',
        endLabel: 'disabled-foreground',
        requiredIndicator: 'disabled-foreground',
        leftSlot: 'disabled-foreground',
      },
    },
    size: {
      sm: {
        leftSlot: [
          'z--form-control-label-sm-slot-width',
          'z--form-control-label-sm-slot-height',
        ],
      },
      md: {
        leftSlot: [
          'z--form-control-label-md-slot-width',
          'z--form-control-label-md-slot-height',
        ],
      },
    },
    alignIcon: {
      start: '',
      center: {
        leftSlot: 'self-center',
      },
    },
  },
});

interface FormControlLabelSlotProps {
  /** Additional props for the content wrapper `<span>` (wraps label row and description). */
  content?: ComponentProps<object, 'span'>;
  /** Additional props for the label text wrapper `<span>`. */
  labelText?: ComponentProps<object, 'span'>;

  /** Additional props for the required indicator `<span>`. */
  requiredIndicator?: ComponentProps<object, 'span'>;
  /** Additional props for the end label `<span>`. */
  endLabel?: ComponentProps<object, 'span'>;
  /** Additional props for the start slot `<span>`. */
  leftSlot?: ComponentProps<object, 'span'>;
}

interface FormControlLabelBaseProps {
  /** The label content. */
  label?: ReactNode;
  /** Whether the field is required. Shows a critical mark (asterisk). */
  isRequired?: boolean;
  /** Whether the field is disabled. */
  isDisabled?: boolean;
  /** Content at the end of the label row (e.g., character count). */
  endLabel?: ReactNode;
  /**
   * The id for the description element, used for `aria-describedby` association.
   */
  descriptionId?: string;
  /**
   * Provide the user with context or feedback for the input.
   */
  description?: FormDescriptionProps['description'];
  /**
   * The error message to display when `isInvalid` is true. Overrides `description` when present.
   */
  errorMessage?: FormDescriptionProps['errorMessage'];
  /**
   * Success message shown when `isSuccess` is true. Overrides `description` when present.
   */
  successMessage?: FormDescriptionProps['successMessage'];
  /**
   * Whether the value is invalid.
   */
  isInvalid?: FormDescriptionProps['isInvalid'];
  /**
   * Whether the field is in a success state.
   */
  isSuccess?: FormDescriptionProps['isSuccess'];
  /** Props for internal sub-elements. */
  slotProps?: FormControlLabelSlotProps;
  /**
   * The id applied to the label text wrapper, used for `aria-labelledby` association.
   * Scopes the accessible name to the label text only, excluding `endLabel`.
   */
  labelTextId?: string;
  /**
   * The id applied to the end label element, used for `aria-describedby` association.
   */
  endLabelId?: string;
  /** Content rendered in the start slot (before label text). */
  leftSlot?: ReactNode;
  /**
   * The colour scheme of the label.
   * @default 'primary'
   */
  color?: 'primary' | 'white';
  /**
   * The size of the form control and its label text.
   * @default 'md'
   */
  size?: 'sm' | 'md';
  /**
   * The vertical alignment of the control icon relative to the label.
   * @default 'start'
   */
  alignIcon?: Variant['alignIcon'];
}

type FormControlLabelProps = ComponentProps<FormControlLabelBaseProps, 'span'>;

type FormControlLabelComponent = Component<FormControlLabelBaseProps, 'span'>;

const FormControlLabel: FormControlLabelComponent = (props) => {
  const {
    label,
    isRequired,
    isDisabled,
    endLabel,
    slotProps,
    leftSlot,
    className,
    labelTextId,
    endLabelId,
    descriptionId,
    description,
    errorMessage,
    successMessage,
    isInvalid,
    isSuccess,
    color = 'primary',
    size,
    alignIcon,
    ref,
  } = props;

  const styles = formControlLabelStyles({ color, isDisabled, alignIcon, size });

  if (!label) {
    return null;
  }

  return (
    <Typography
      component="span"
      variant={resolveVariant(size)}
      ref={ref}
      className={styles.root({ className })}
    >
      {leftSlot && (
        <span
          {...slotProps?.leftSlot}
          className={styles.leftSlot({
            className: slotProps?.leftSlot?.className,
          })}
        >
          {leftSlot}
        </span>
      )}
      <span
        {...slotProps?.content}
        className={styles.content({
          className: slotProps?.content?.className,
        })}
      >
        <span className={styles.label()}>
          <Typography
            {...slotProps?.labelText}
            component="span"
            variant="inherit"
            color="inherit"
            id={labelTextId}
            className={styles.labelText({
              className: slotProps?.labelText?.className,
            })}
          >
            {label}
            {isRequired && (
              <Typography
                {...slotProps?.requiredIndicator}
                component="span"
                variant="inherit"
                color="inherit"
                className={styles.requiredIndicator({
                  className: slotProps?.requiredIndicator?.className,
                })}
                aria-hidden="true"
              >
                *
              </Typography>
            )}
          </Typography>
          {endLabel && (
            <Typography
              {...slotProps?.endLabel}
              component="span"
              variant="inherit"
              color="inherit"
              id={endLabelId}
              className={styles.endLabel({
                className: slotProps?.endLabel?.className,
              })}
            >
              {endLabel}
            </Typography>
          )}
        </span>
        <FormDescription
          id={descriptionId}
          color={color}
          description={description}
          errorMessage={errorMessage}
          successMessage={successMessage}
          isInvalid={isInvalid}
          isSuccess={isSuccess}
          isDisabled={isDisabled}
          className={styles.description()}
        />
      </span>
    </Typography>
  );
};

FormControlLabel.displayName = 'FormControlLabel';

export {
  FormControlLabel,
  type FormControlLabelProps,
  type FormControlLabelBaseProps,
  type FormControlLabelSlotProps,
  formControlLabelClasses,
};
